/**
 * Model suggestions by brand and category
 * Used for autocomplete in the listing form
 */
type ModelSuggestionsMap = Record<string, Record<string, string[]>>;

export const modelSuggestionsByBrandAndCategory: ModelSuggestionsMap = {
  // Electronics - Smart TVs
  LG: {
    smart_tvs: [
      "OLED55C1PUB",
      "OLED65C1PUB",
      "OLED77C1PUB",
      "NANO75",
      "NANO85",
      "UN7300",
      "UN8500",
      "QNED90",
      "QNED95",
    ],
    air_conditioners: [
      "Standard Plus",
      "Artcool Mirror",
      "Dual Inverter",
      "Window AC",
    ],
    washing_machines: ["T2107VS2M", "F2514STTSW", "WM3250HWA"],
  },
  Samsung: {
    smart_tvs: [
      "QN90A",
      "QN85A",
      "AU8000",
      "TU8000",
      "The Frame",
      "QLED Q60A",
      "QLED Q70A",
    ],
    air_conditioners: ["Wind-Free", "Digital Inverter", "AR12", "AR18"],
    washing_machines: ["WW90T534DAW", "WD90T654DBH", "WA11F5S2UWW"],
  },
  Sony: {
    smart_tvs: ["X90J", "X85J", "X80J", "A80J", "A90J"],
  },
  Apple: {
    iphones: [
      "iPhone 13 Pro Max",
      "iPhone 13 Pro",
      "iPhone 13",
      "iPhone 12 Pro Max",
      "iPhone 12 Pro",
      "iPhone 12",
      "iPhone 11 Pro Max",
      "iPhone 11 Pro",
      "iPhone 11",
      "iPhone SE",
      "iPhone XS Max",
      "iPhone XS",
      "iPhone XR",
    ],
    iphone: [
      "iPhone 13 Pro Max",
      "iPhone 13 Pro",
      "iPhone 13",
      "iPhone 12 Pro Max",
      "iPhone 12 Pro",
      "iPhone 12",
      "iPhone 11 Pro Max",
      "iPhone 11 Pro",
      "iPhone 11",
      "iPhone SE",
      "iPhone XS Max",
      "iPhone XS",
      "iPhone XR",
    ],
    ipads: ["iPad Pro 12.9", "iPad Pro 11", "iPad Air", "iPad", "iPad mini"],
    ipad: ["iPad Pro 12.9", "iPad Pro 11", "iPad Air", "iPad", "iPad mini"],
    macbooks: [
      "MacBook Pro 16",
      "MacBook Pro 14",
      "MacBook Pro 13",
      "MacBook Air M1",
      "MacBook Air M2",
    ],
    mac: [
      "MacBook Pro 16",
      "MacBook Pro 14",
      "MacBook Pro 13",
      "MacBook Air M1",
      "MacBook Air M2",
    ],
    iwatch: ["Apple Watch Series 7", "Apple Watch Series 6", "Apple Watch SE"],
    "smart-watch": [
      "Apple Watch Series 7",
      "Apple Watch Series 6",
      "Apple Watch SE",
    ],
  },
  Tecno: {
    mobile_phones: ["Camon 18", "Spark 8", "Pova 3", "Phantom X", "Camon 17"],
    "android-phone": ["Camon 18", "Spark 8", "Pova 3", "Phantom X", "Camon 17"],
  },
  Infinix: {
    mobile_phones: ["Note 11", "Hot 11", "Zero X", "Smart 6"],
    "android-phone": ["Note 11", "Hot 11", "Zero X", "Smart 6"],
  },
  Firman: {
    generators: [
      "Firman 3300W",
      "Firman 5000W",
      "Firman 7500W",
      "Firman 9500W",
      "Firman 11000W",
    ],
  },
  HP: {
    laptops: ["Pavilion", "Envy", "Spectre", "EliteBook", "ProBook"],
    "win-pc": ["Pavilion", "Envy", "Spectre", "EliteBook", "ProBook"],
    desktops: ["Pavilion Desktop", "EliteDesk", "ProDesk"],
  },
  Dell: {
    laptops: ["XPS 13", "XPS 15", "Inspiron", "Latitude", "Alienware"],
    "win-pc": ["XPS 13", "XPS 15", "Inspiron", "Latitude", "Alienware"],
    desktops: ["OptiPlex", "XPS Desktop", "Alienware Aurora"],
  },
  Lenovo: {
    laptops: ["ThinkPad", "IdeaPad", "Yoga", "Legion"],
    "win-pc": ["ThinkPad", "IdeaPad", "Yoga", "Legion"],
  },
  PlayStation: {
    gaming_consoles: [
      "PlayStation 5",
      "PlayStation 4 Pro",
      "PlayStation 4",
      "PlayStation 3",
    ],
    "play-station": [
      "PlayStation 5",
      "PlayStation 4 Pro",
      "PlayStation 4",
      "PlayStation 3",
    ],
  },
  Xbox: {
    gaming_consoles: [
      "Xbox Series X",
      "Xbox Series S",
      "Xbox One X",
      "Xbox One S",
      "Xbox One",
    ],
  },
  Nintendo: {
    gaming_consoles: [
      "Nintendo Switch OLED",
      "Nintendo Switch",
      "Nintendo Switch Lite",
      "Nintendo 3DS",
    ],
  },
  // Kitchen brands
  "Haier Thermocool": {
    standing_fridges: ["HFR-200L", "HFR-300L", "HFR-400L"],
    inverter_fridge: ["HFR-220L", "HFR-280L"],
  },
  Scanfrost: {
    standing_fridges: ["SF-180L", "SF-200L", "SF-250L"],
  },
};

/**
 * Get model suggestions for a brand and subcategory
 */
export function getModelSuggestions(
  brand: string | null | undefined,
  subcategory: string | null | undefined,
): string[] {
  if (!brand || !subcategory) return [];

  const normalizedBrand = brand.trim();
  const normalizedSubcategory = subcategory.toLowerCase().replace(/\s+/g, "_");

  const brandModels = modelSuggestionsByBrandAndCategory[normalizedBrand];
  if (!brandModels) return [];

  return brandModels[normalizedSubcategory] || [];
}

/**
 * Search models for a brand and subcategory
 */
export function searchModels(
  searchTerm: string,
  brand: string | null | undefined,
  subcategory: string | null | undefined,
): string[] {
  const suggestions = getModelSuggestions(brand, subcategory);
  if (!searchTerm.trim()) return suggestions.slice(0, 10);

  const normalizedSearch = searchTerm.toLowerCase();
  return suggestions
    .filter((model) => model.toLowerCase().includes(normalizedSearch))
    .slice(0, 10);
}
